import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Navbar from '../../navbar';
import Header from '../../header';
import SignatureCanvas from 'react-signature-canvas';
import { getAttachments, getSignature } from '../../../store/reducers/fileSlice';
import { updateApplicationData } from '../../../store/reducers/applicationSlice';
import { showError, showSuccess } from '../../../helpers/toast';

const AddAttachment = forwardRef((props, ref) => {
    const { applicationId, userId, signatureIds, setUploadedFiles, uploadedFiles, applicationState, applicationName, jsonData, signatureImage: propSignatureImage, onSignatureUpdate } = props;
    const [affidavitFile, setAffidavitFile] = useState(null);
    const [supportedDocuments, setSupportedDocuments] = useState({});
    const [signatureImage, setSignatureImage] = useState(null);
    const [showSignaturePad, setShowSignaturePad] = useState(true);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [existingSignature, setExistingSignature] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [saving, setSaving] = useState(false);
    const sigCanvas = useRef();

    const isInternalUpdate = useRef(false); // Track if the update is internal
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const dispatch = useDispatch();

    // Handle propSignatureImage changes
    useEffect(() => {
        if (propSignatureImage) {
            console.log('Received signatureImage prop:', propSignatureImage);
            // Convert data URL to File object if it's a string
            if (typeof propSignatureImage === 'string' && propSignatureImage.startsWith('data:')) {
                fetch(propSignatureImage)
                    .then(res => res.blob())
                    .then(blob => {
                        const signatureFile = new File([blob], 'formSignatureUser.png', { type: 'image/png' });
                        console.log('Converted signatureImage to File:', signatureFile);
                        setSignatureImage(signatureFile);
                    })
                    .catch(error => {
                        console.error('Error converting signatureImage to File:', error);
                        // Fallback: set as is if conversion fails
                        setSignatureImage(propSignatureImage);
                    });
            } else {
                setSignatureImage(propSignatureImage);
            }
        }
    }, [propSignatureImage]);

    // Load existing attachments when component mounts or props change
    useEffect(() => {
        const loadExistingAttachments = async () => {
            if (!applicationId || !userId || !authToken) {
                return;
            }

            try {
                setLoading(true);
                console.log('Loading attachments and signature for application:', applicationId);

                // Fetch both attachments and signature concurrently with proper error handling
                const [attachmentFiles, signatureData] = await Promise.allSettled([
                    getAttachments({
                        userId: userId,
                        applicationId: applicationId,
                        authToken: authToken
                    }),
                    getSignature({
                        userId: userId,
                        applicationId: applicationId,
                        authToken: authToken
                    })
                ]);

                // Handle results from Promise.allSettled
                const attachmentResult = attachmentFiles.status === 'fulfilled' ? attachmentFiles.value : [];
                const signatureResult = signatureData.status === 'fulfilled' ? signatureData.value : null;

                if (attachmentFiles.status === 'rejected') {
                    const error = attachmentFiles.reason;
                    if (error.message === 'NOT_FOUND') {
                        console.log('No attachments found for this application (404 expected)');
                    } else if (error.message === 'SERVER_ERROR') {
                        console.log('Server error loading attachments (500) - will retry later');
                    } else {
                        console.log('Error loading attachments:', error.message);
                    }
                }
                if (signatureData.status === 'rejected') {
                    const error = signatureData.reason;
                    if (error.message === 'NOT_FOUND') {
                        console.log('No signature found for this application (404 expected)');
                    } else if (error.message === 'SERVER_ERROR') {
                        console.log('Server error loading signature (500) - will retry later');
                    } else {
                        console.log('Error loading signature:', error.message);
                    }
                }

                setExistingAttachments(Array.isArray(attachmentResult) ? attachmentResult : []);
                setExistingSignature(signatureResult || null);

                console.log('Loaded attachments:', attachmentResult);
                console.log('Loaded signature data:', signatureResult);
                console.log('Signature data type:', typeof signatureResult);
                console.log('Signature data length:', signatureResult ? signatureResult.length : 'null');

                // Update parent's uploadedFiles with existing attachments
                if (Array.isArray(attachmentResult) && attachmentResult.length > 0) {
                    const existingFiles = attachmentResult.map(filename => ({ name: filename }));
                    console.log('Updating parent uploadedFiles with existing attachments:', existingFiles);
                    setUploadedFiles(prevFiles => {
                        // Merge existing files with current uploadedFiles, avoiding duplicates
                        const existingFileNames = new Set(existingFiles.map(f => f.name));
                        const newFiles = prevFiles.filter(f => !existingFileNames.has(f.name));
                        return [...newFiles, ...existingFiles];
                    });
                }

                // If signature exists, load it to canvas
                if (signatureResult) {
                    console.log('Loading existing signature:', signatureResult);
                    try {
                        // Convert base64 to file for consistency
                        const response = await fetch(signatureResult);
                        const blob = await response.blob();
                        const signatureFile = new File([blob], 'formSignatureUser.png', { type: 'image/png' });
                        setSignatureImage(signatureFile);
                        loadSignatureToCanvas(signatureFile);
                        console.log('Signature loaded successfully');

                        // Update parent's uploadedFiles with signature
                        setUploadedFiles(prevFiles => {
                            const filteredFiles = prevFiles.filter(f => !f.name.includes('formSignatureUser'));
                            return [...filteredFiles, signatureFile];
                        });
                    } catch (error) {
                        console.error('Error loading signature:', error);
                        // If fetch fails, signatureData might be a base64 string
                        // Try to convert it directly
                        try {
                            const base64Data = signatureResult.split(',')[1] || signatureResult;
                            const byteCharacters = atob(base64Data);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: 'image/png' });
                            const signatureFile = new File([blob], 'formSignatureUser.png', { type: 'image/png' });
                            setSignatureImage(signatureFile);
                            loadSignatureToCanvas(signatureFile);
                            console.log('Signature loaded from base64 string');

                            // Update parent's uploadedFiles with signature
                            setUploadedFiles(prevFiles => {
                                const filteredFiles = prevFiles.filter(f => !f.name.includes('formSignatureUser'));
                                return [...filteredFiles, signatureFile];
                            });
                        } catch (base64Error) {
                            console.error('Error loading signature from base64:', base64Error);
                        }
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Error loading existing attachments:', err);
                setLoading(false);
            }
        };

        loadExistingAttachments();
    }, [applicationId, userId, authToken, setUploadedFiles]);

    // Cleanup effect to handle component unmounting
    useEffect(() => {
        const canvasRef = sigCanvas.current;
        return () => {
            // Clear any pending operations when component unmounts
            if (canvasRef) {
                canvasRef.clear();
            }
        };
    }, []);

    useEffect(() => {
        if (showSignaturePad && signatureImage) {
            loadSignatureToCanvas(signatureImage);
        }
    }, [showSignaturePad, signatureImage])
    // Sync state with uploadedFiles
    useEffect(() => {
        if (!isInternalUpdate.current && uploadedFiles) {
            if (uploadedFiles && uploadedFiles.length > 0) {
                console.log("Uploaded File Names:");
                uploadedFiles.forEach((file, index) => {
                    console.log(`${index + 1}: ${file.name}`);
                });
            }
            const newAffidavitFile = uploadedFiles.find((file) =>
                file.name.startsWith('formAffidavitFile')
            );

            // Merge uploadedFiles with existing supportedDocuments using functional update
            const uploadedSupportedDocs = uploadedFiles
                .filter((file) => file.name.startsWith('supportedDocument'));

            setSupportedDocuments((prevSupportedDocs) => {
                // Start with existing supportedDocuments to preserve files that were just added
                const existingSupportedDocs = { ...prevSupportedDocs };

                // Extract numeric keys from existing documents to maintain continuity
                const existingKeys = Object.keys(existingSupportedDocs).map(key => {
                    const match = key.match(/\d+/);
                    return match ? parseInt(match[0]) : 0;
                });
                const maxExistingKey = existingKeys.length > 0 ? Math.max(...existingKeys) : 0;

                // Create a map of existing files by name for quick lookup
                const existingFilesByName = new Map();
                Object.entries(existingSupportedDocs).forEach(([key, file]) => {
                    if (file && file.name) {
                        existingFilesByName.set(file.name, key);
                    }
                });

                // Build new supportedDocuments: preserve existing, add new from uploadedFiles
                const newSupportedDocuments = { ...existingSupportedDocs };
                let nextKey = maxExistingKey;

                uploadedSupportedDocs.forEach((file) => {
                    // Check if this file already exists in supportedDocuments by name
                    const existingKey = existingFilesByName.get(file.name);

                    if (existingKey) {
                        // Update existing file (in case it changed)
                        newSupportedDocuments[existingKey] = file;
                    } else {
                        // Add new file with next available key
                        nextKey++;
                        newSupportedDocuments[nextKey] = file;
                    }
                });

                return newSupportedDocuments;
            });

            const newSignatureImage = uploadedFiles.find((file) =>
                file.name.startsWith('formSignatureUser')
            );

            setAffidavitFile(newAffidavitFile || null);
            setSignatureImage(newSignatureImage || null);
            if (newSignatureImage) {
                loadSignatureToCanvas(newSignatureImage);
            }
        }

        // Reset internal update flag after syncing
        isInternalUpdate.current = false;
    }, [uploadedFiles]);

    // Sync state changes to uploadedFiles
    useEffect(() => {
        const filteredPreviousFiles = uploadedFiles?.filter(
            (file) => !file.name.startsWith('formSignatureUser') &&
                !file.name.startsWith('supportedDocument') &&
                !file.name.startsWith('formAffidavitFile')
        ) || [];

        let newFiles = [
            ...filteredPreviousFiles.filter(Boolean),
            affidavitFile,
            ...Object.values(supportedDocuments),
            signatureImage,
        ].filter(Boolean);

        // Deduplicate by file.name
        const seen = new Set();
        newFiles = newFiles.filter(file => {
            if (!file) return false;
            if (seen.has(file.name)) return false;
            seen.add(file.name);
            return true;
        });

        // Only update if the list actually changed in terms of names
        const oldNames = (uploadedFiles || []).map(f => f.name).join(',');
        const newNames = newFiles.map(f => f.name).join(',');
        if (oldNames !== newNames) {
            isInternalUpdate.current = true;
            setUploadedFiles(newFiles);
        }
    }, [affidavitFile, supportedDocuments, signatureImage, uploadedFiles, setUploadedFiles]);


    const validateFile = (file, allowedTypes = [], maxSizeMB = 18) => {
        if (!allowedTypes.includes(file.type)) {
            showError('Invalid file type.');
            return false;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
            showError(`File size exceeds the ${maxSizeMB}MB limit.`);
            return false;
        }
        return true;
    };

    const renameFile = (file, prefix) => {
        return new File([file], `${prefix + "_" + file.name}`, { type: file.type });
    };

    const handleAffidavitChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile && validateFile(selectedFile, ['application/pdf', 'image/png', 'image/jpeg'])) {
            const renamedFile = renameFile(selectedFile, 'formAffidavitFile');
            setAffidavitFile(renamedFile);
            // Auto-save when new file is selected (disabled for now)
            // setTimeout(() => saveChanges(), 100);
        }
    };

    const handleMultipleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setSupportedDocuments((prevFiles) => {
            // Find the maximum existing key to continue from
            const existingKeys = Object.keys(prevFiles).map(key => {
                // Extract numeric part from keys like "doc1", "1", etc.
                const match = key.match(/\d+/);
                return match ? parseInt(match[0]) : 0;
            });
            const maxKey = existingKeys.length > 0 ? Math.max(...existingKeys) : 0;

            // Create new files with keys starting from maxKey + 1
            const newFiles = selectedFiles.reduce((acc, file, index) => {
                const newKey = maxKey + index + 1;
                const renamedFile = renameFile(file, `supportedDocument_${newKey}`);
                acc[newKey] = renamedFile;
                return acc;
            }, {});

            // Merge with existing files (new files won't overwrite existing ones)
            return { ...prevFiles, ...newFiles };
        });

        // Reset input value to allow re-selection of the same file if needed
        e.target.value = '';

        // Auto-save when new files are selected (disabled for now)
        // setTimeout(() => saveChanges(), 100);
    };

    const removeFile = (fileIndex) => {
        // Check if the file to remove is an existing one that needs to be tracked for deletion
        const fileToRemove = supportedDocuments[fileIndex];
        if (fileToRemove && existingAttachments.includes(fileToRemove.name)) {
            console.log('Marking file for deletion:', fileToRemove.name);
            setFilesToDelete(prev => [...prev, fileToRemove.name]);
        }

        setSupportedDocuments((prevFiles) => {
            const updatedFiles = { ...prevFiles };
            delete updatedFiles[fileIndex];
            return updatedFiles;
        });
        // Auto-save when file is removed (disabled for now)
        // setTimeout(() => saveChanges(), 100);
    };

    const saveSignature = () => {
        if (!sigCanvas.current) {
            console.warn('Signature canvas is not available');
            return;
        }

        console.log('Saving signature...');
        const trimmedDataUrl = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        // Convert DataURL to Blob (binary large object)
        fetch(trimmedDataUrl)
            .then(res => res.blob())
            .then(blob => {
                const signatureFile = new File([blob], 'formSignatureUser.png', { type: 'image/png' });
                console.log('Signature file created:', signatureFile);

                // Pass the file to the parent component or handle it here
                setSignatureForm(signatureFile);
                setSignatureImage(signatureFile);
                console.log('Signature image state updated');

                // Notify parent component about signature update
                if (onSignatureUpdate) {
                    console.log('Notifying parent component about signature update');
                    onSignatureUpdate(signatureFile);
                }

                // Auto-save when signature is saved (disabled for now)
                // setTimeout(() => saveChanges(), 100);

            })
            .catch(error => {
                console.error('Error saving signature:', error);
            });
    };
    const setSignatureForm = (file) => {
        console.log('setSignatureForm called with file:', file);
        console.log('signatureIds:', signatureIds);
        const reader = new FileReader();
        reader.onload = (e) => {
            console.log('FileReader onload, setting signature for signatureIds:', signatureIds);
            signatureIds.forEach(id => {
                const imageInput = document.getElementById(id);
                console.log('Looking for image input with id:', id, 'found:', imageInput);
                if (imageInput && imageInput.type === 'image') {
                    imageInput.src = e.target.result;
                    console.log('Set signature src for id:', id);
                }
            });
        };
        reader.readAsDataURL(file);
    }

    const clearCanvas = () => {
        if (sigCanvas.current) {
            sigCanvas.current.clear();
        }
    };

    const handleSignatureChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile && validateFile(selectedFile, ['image/png', 'image/jpeg'])) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);

                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    // Make white (or near white) pixels transparent
                    for (let i = 0; i < data.length; i += 4) {
                        const r = data[i];
                        const g = data[i + 1];
                        const b = data[i + 2];

                        // Adjust this threshold as needed (here: remove light grays too)
                        if (r > 240 && g > 240 && b > 240) {
                            data[i + 3] = 0; // Set alpha to 0
                        }
                    }

                    ctx.putImageData(imageData, 0, 0);

                    canvas.toBlob((blob) => {
                        const transparentFile = new File([blob], 'formSignatureUser.png', {
                            type: 'image/png',
                        });

                        setSignatureImage(transparentFile);
                        setSignatureForm(transparentFile);
                        loadSignatureToCanvas(transparentFile);

                        // Auto-save when signature file is uploaded (disabled for now)
                        // setTimeout(() => saveChanges(), 100);
                    }, 'image/png');
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(selectedFile);
        }
    };

    const loadSignatureToCanvas = (file) => {
        if (!file) return;

        setShowSignaturePad(true);

        // Check if file is a File object or a base64 string
        if (file instanceof File) {
            // Handle File object
            const reader = new FileReader();
            reader.onload = (e) => {
                const image = new Image();
                image.onload = () => {
                    // Check if sigCanvas.current exists and component is still mounted
                    if (sigCanvas.current && sigCanvas.current.getCanvas) {
                        try {
                            sigCanvas.current.clear(); // Clear existing canvas content
                            const ctx = sigCanvas.current.getCanvas().getContext('2d');
                            if (ctx) {
                                ctx.drawImage(image, 0, 0, sigCanvas.current.getCanvas().width, sigCanvas.current.getCanvas().height);
                            }
                        } catch (error) {
                            console.warn('Error loading signature to canvas:', error);
                        }
                    }
                };
                image.src = e.target.result;
            };
            reader.readAsDataURL(file);
        } else if (typeof file === 'string') {
            // Handle base64 string
            const image = new Image();
            image.onload = () => {
                // Check if sigCanvas.current exists and component is still mounted
                if (sigCanvas.current && sigCanvas.current.getCanvas) {
                    try {
                        sigCanvas.current.clear(); // Clear existing canvas content
                        const ctx = sigCanvas.current.getCanvas().getContext('2d');
                        if (ctx) {
                            ctx.drawImage(image, 0, 0, sigCanvas.current.getCanvas().width, sigCanvas.current.getCanvas().height);
                        }
                    } catch (error) {
                        console.warn('Error loading signature to canvas:', error);
                    }
                }
            };
            image.src = file; // Use the base64 string directly
        } else {
            console.warn('Invalid file type for loadSignatureToCanvas:', typeof file);
        }
    };


    // Function to save all changes
    const saveChanges = async () => {
        try {
            setSaving(true);

            // Prepare files to upload (only new files that were actually selected)
            const newFiles = [
                affidavitFile,
                ...Object.values(supportedDocuments),
                signatureImage,
            ].filter(Boolean);

            const existingFilesToPreserve = existingAttachments
                .filter(file => !filesToDelete.includes(file))
                .map(filename => {
                    return {
                        name: filename,
                        Type: 'existing' // Mark as existing file
                    };
                });

            const allFiles = [...newFiles, ...existingFilesToPreserve];

            const updateData = {
                id: applicationId,
                userId: userId,
                token: authToken,
                files: allFiles, // Include both new and existing files
                form: applicationName || 'attachments', // Use application name or fallback
                json_data: jsonData || {}, // Include form data if available
                state: applicationState, // Include application state
            };

            console.log('=== ATTACHMENTS UPDATE DEBUG ===');
            console.log('Application ID:', applicationId);
            console.log('User ID:', userId);
            console.log('Application State:', applicationState);
            console.log('Application Name:', applicationName);
            console.log('JSON Data (jsonData prop):', jsonData);
            console.log('JSON Data type:', typeof jsonData);
            console.log('JSON Data length:', jsonData ? Object.keys(jsonData).length : 'null/undefined');
            console.log('Files count:', allFiles.length);
            console.log('Signature Image:', signatureImage);
            console.log('Signature Image type:', typeof signatureImage);
            console.log('New Files:', newFiles);
            console.log('Existing Files to Preserve:', existingFilesToPreserve);
            console.log('All Files:', allFiles);
            console.log('State being sent:', updateData.state);
            console.log('Update data:', updateData);

            // Dispatch the update action
            await dispatch(updateApplicationData(updateData)).unwrap();

            // Update parent's uploadedFiles with the current state
            const currentFiles = [
                affidavitFile,
                ...Object.values(supportedDocuments),
                signatureImage,
            ].filter(Boolean);

            console.log('Updating parent uploadedFiles with current files:', currentFiles);
            setUploadedFiles(currentFiles);

            // Clear files to delete after successful update
            setFilesToDelete([]);

            // Reload attachments to reflect changes
            const [attachmentFiles, signatureData] = await Promise.all([
                getAttachments({
                    userId: userId,
                    applicationId: applicationId,
                    authToken: authToken
                }),
                getSignature({
                    userId: userId,
                    applicationId: applicationId,
                    authToken: authToken
                }).catch(() => null)
            ]);

            setExistingAttachments(Array.isArray(attachmentFiles) ? attachmentFiles : []);
            setExistingSignature(signatureData || null);

            console.log('Files updated successfully');

            // Notify parent component about signature update if signature was saved
            if (signatureImage && onSignatureUpdate) {
                console.log('Notifying parent component about signature update after save');
                onSignatureUpdate(signatureImage);
            }

            // Refresh the page or reload data to show updated files
            showSuccess('Changes saved successfully');

        } catch (error) {
            console.error('Error saving changes:', error);
            showError(`Failed to save changes: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    // Expose saveChanges function to parent component
    useImperativeHandle(ref, () => ({
        saveChanges: saveChanges
    }));

    // Comprehensive file name trimming utility function
    const trimFileName = (filename, maxLength = 30) => {
        if (!filename) return '';

        let trimmed = filename;

        // Remove common prefixes
        const prefixesToRemove = [
            /^supportedDocument_\d+_/,
            /^formAffidavitFile_/,
            /^formSignatureUser_/,
            /^attachment_/,
            /^file_/,
            /^doc_/,
            /^document_/,
            /^\d+_/, // Remove leading numbers followed by underscore
        ];

        // Remove prefixes
        prefixesToRemove.forEach(prefix => {
            trimmed = trimmed.replace(prefix, '');
        });

        // Remove common suffixes before file extension
        const suffixesToRemove = [
            /_copy$/,
            /_backup$/,
            /_old$/,
            /_new$/,
            /_temp$/,
            /_tmp$/,
            /_draft$/,
            /_final$/,
            /_v\d+$/, // Remove version numbers like _v1, _v2, etc.
            /_\d{4}-\d{2}-\d{2}$/, // Remove dates like _2024-01-15
            /_\d{8}$/, // Remove dates like _20240115
        ];

        // Get file extension
        const lastDotIndex = trimmed.lastIndexOf('.');
        const extension = lastDotIndex > -1 ? trimmed.substring(lastDotIndex) : '';
        const nameWithoutExt = lastDotIndex > -1 ? trimmed.substring(0, lastDotIndex) : trimmed;

        // Remove suffixes from name part
        let cleanName = nameWithoutExt;
        suffixesToRemove.forEach(suffix => {
            cleanName = cleanName.replace(suffix, '');
        });

        // Remove special characters and clean up
        cleanName = cleanName
            .replace(/[_-]+/g, ' ') // Replace multiple underscores/hyphens with single space
            .replace(/^\s+|\s+$/g, '') // Trim whitespace
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/[^\w\s.-]/g, '') // Remove special characters except word chars, spaces, dots, hyphens
            .trim();

        // If name is empty after cleaning, use original filename
        if (!cleanName) {
            cleanName = nameWithoutExt;
        }

        // Limit length and add ellipsis if needed
        if (cleanName.length > maxLength) {
            cleanName = cleanName.substring(0, maxLength - 3) + '...';
        }

        return cleanName + extension;
    };

    if (loading) {
        return (
            <div className='w-100'>
                <Navbar />
                <Header />
                <div className="attachment-wrapper">
                    <div className="attachment mb-5">
                        <h2 className='text-uppercase'>Attachments</h2>
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <div className="spinner"></div>
                            <p>Loading attachments...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbar />
            <Header />
            <div className="attachment-wrapper p-5">
                <div className="attachment mb-5">
                    <h2 className='text-uppercase'>Required Attachments</h2>
                    <div className="fileUpload">
                        {/* Affidavit File Upload */}
                        <div className="form-group">
                            <label htmlFor="file-1" className='mb-2'>Attach Affidavit</label>
                            <input
                                type="file"
                                name="file"
                                id="file-1"
                                className="input-file"
                                onChange={handleAffidavitChange}
                            />
                            <label htmlFor="file-1" className="btn btn-upload d-flex align-items-center gap-2 justify-content-center">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.25 2.96905H5.40469V8.25499C5.40469 8.32374 5.46094 8.37999 5.52969 8.37999H6.46719C6.53594 8.37999 6.59219 8.32374 6.59219 8.25499V2.96905H7.75C7.85469 2.96905 7.9125 2.84874 7.84844 2.76749L6.09844 0.551867C6.08674 0.536927 6.0718 0.524844 6.05475 0.516534C6.03769 0.508225 6.01897 0.503906 6 0.503906C5.98103 0.503906 5.96231 0.508225 5.94525 0.516534C5.92819 0.524844 5.91326 0.536927 5.90156 0.551867L4.15156 2.76593C4.0875 2.84874 4.14531 2.96905 4.25 2.96905ZM11.7188 7.78624H10.7812C10.7125 7.78624 10.6562 7.84249 10.6562 7.91124V10.3175H1.34375V7.91124C1.34375 7.84249 1.2875 7.78624 1.21875 7.78624H0.28125C0.2125 7.78624 0.15625 7.84249 0.15625 7.91124V11.005C0.15625 11.2816 0.379687 11.505 0.65625 11.505H11.3438C11.6203 11.505 11.8438 11.2816 11.8438 11.005V7.91124C11.8438 7.84249 11.7875 7.78624 11.7188 7.78624Z" fill="black" fillOpacity="0.25" />
                                </svg>

                                <span
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {affidavitFile ? trimFileName(affidavitFile.name, 25) : 'Attach Affidavit'}
                                </span>
                                {affidavitFile && (
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent label click from firing file input
                                            e.preventDefault();  // Just in case

                                            if (affidavitFile && existingAttachments.includes(affidavitFile.name)) {
                                                console.log('Marking affidavit for deletion:', affidavitFile.name);
                                                setFilesToDelete(prev => [...prev, affidavitFile.name]);
                                            }

                                            setAffidavitFile(null);
                                        }}
                                        title="Remove Affidavit"
                                        style={{
                                            marginLeft: '8px',
                                            color: '#ff4d4d',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            flexShrink: 0,
                                        }}
                                    >
                                        🗑️
                                    </span>
                                )}
                            </label>
                        </div>


                        {/* Supported Documents Upload */}
                        <div className="form-group">
                            <label htmlFor="file-2" className='mb-2'>Attach Supported Documents</label>
                            <input
                                type="file"
                                name="file"
                                id="file-2"
                                className="input-file"
                                multiple
                                onChange={handleMultipleFileChange}
                            />
                            <label htmlFor="file-2" className="btn btn-upload d-flex align-items-center gap-2 justify-content-center">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.25 2.96905H5.40469V8.25499C5.40469 8.32374 5.46094 8.37999 5.52969 8.37999H6.46719C6.53594 8.37999 6.59219 8.32374 6.59219 8.25499V2.96905H7.75C7.85469 2.96905 7.9125 2.84874 7.84844 2.76749L6.09844 0.551867C6.08674 0.536927 6.0718 0.524844 6.05475 0.516534C6.03769 0.508225 6.01897 0.503906 6 0.503906C5.98103 0.503906 5.96231 0.508225 5.94525 0.516534C5.92819 0.524844 5.91326 0.536927 5.90156 0.551867L4.15156 2.76593C4.0875 2.84874 4.14531 2.96905 4.25 2.96905ZM11.7188 7.78624H10.7812C10.7125 7.78624 10.6562 7.84249 10.6562 7.91124V10.3175H1.34375V7.91124C1.34375 7.84249 1.2875 7.78624 1.21875 7.78624H0.28125C0.2125 7.78624 0.15625 7.84249 0.15625 7.91124V11.005C0.15625 11.2816 0.379687 11.505 0.65625 11.505H11.3438C11.6203 11.505 11.8438 11.2816 11.8438 11.005V7.91124C11.8438 7.84249 11.7875 7.78624 11.7188 7.78624Z" fill="black" fillOpacity="0.25" />
                                </svg>

                                <span>Attach Supported Documents</span>
                            </label>
                            <ul className="file-list">
                                {Object.entries(supportedDocuments).map(([key, file]) => (
                                    <li key={key} className="file-item">
                                        {trimFileName(file.name, 30)}
                                        <button
                                            className="remove-btn"
                                            onClick={() => removeFile(key)}
                                            style={{
                                                marginLeft: '10px',
                                                color: '#ff4d4d',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="form-group ">
                            <label htmlFor="file-signature" className='mb-2'>
                                Attach Signature
                            </label>
                            <input
                                type="file"
                                name="file"
                                id="signature"
                                className="input-file"
                                onChange={handleSignatureChange}
                            />

                            <label htmlFor="signature" className="btn btn-upload d-flex align-items-center gap-2 justify-content-center">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.25 2.96905H5.40469V8.25499C5.40469 8.32374 5.46094 8.37999 5.52969 8.37999H6.46719C6.53594 8.37999 6.59219 8.32374 6.59219 8.25499V2.96905H7.75C7.85469 2.96905 7.9125 2.84874 7.84844 2.76749L6.09844 0.551867C6.08674 0.536927 6.0718 0.524844 6.05475 0.516534C6.03769 0.508225 6.01897 0.503906 6 0.503906C5.98103 0.503906 5.96231 0.508225 5.94525 0.516534C5.92819 0.524844 5.91326 0.536927 5.90156 0.551867L4.15156 2.76593C4.0875 2.84874 4.14531 2.96905 4.25 2.96905ZM11.7188 7.78624H10.7812C10.7125 7.78624 10.6562 7.84249 10.6562 7.91124V10.3175H1.34375V7.91124C1.34375 7.84249 1.2875 7.78624 1.21875 7.78624H0.28125C0.2125 7.78624 0.15625 7.84249 0.15625 7.91124V11.005C0.15625 11.2816 0.379687 11.505 0.65625 11.505H11.3438C11.6203 11.505 11.8438 11.2816 11.8438 11.005V7.91124C11.8438 7.84249 11.7875 7.78624 11.7188 7.78624Z" fill="black" fillOpacity="0.25" />
                                </svg>

                                <span>
                                    {signatureImage ? trimFileName(signatureImage.name, 25) : 'Attach Signature Image'}
                                </span>

                            </label>
                            <div className="boardCol">
                                <div className="boardTitle"><span>Attach signature as PNG with transparent background.</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Existing Attachments Section */}
                {/* {existingAttachments.length > 0 && (
                    <div className="attachment mb-5">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className='text-uppercase mb-0'>Existing Attachments</h2>
                            <button
                                className="btn btn-success"
                                onClick={saveChanges}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50%' }}>File</th>
                                        <th style={{ width: '15%' }}>Download</th>
                                        <th style={{ width: '15%' }}>Remove</th>
                                        <th style={{ width: '20%' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingAttachments.map((filename, index) => (
                                        <tr key={index}>
                                            <td style={{ wordBreak: 'break-word' }}>
                                                <span style={{ fontSize: '1.25rem', marginRight: 8 }}>
                                                    {getFileIcon(filename)}
                                                </span>
                                                {trimFileName(filename, 40)}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => downloadExistingFile(filename)}
                                                    disabled={saving}
                                                >
                                                    Download
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => removeExistingFile(filename)}
                                                    disabled={saving}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                            <td>
                                                {filesToDelete.includes(filename) ? (
                                                    <span className="text-danger">Marked for deletion</span>
                                                ) : (
                                                    <span className="text-success">Active</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )} */}

                {/* Existing Signature Section */}
                {/* {existingSignature && (
                    <div className="attachment mb-5">
                        <h2 className='text-uppercase'>Existing Digital Signature</h2>
                        <div className="card">
                            <div className="card-body text-center">
                                <img src={existingSignature} alt="Existing Digital Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                            </div>
                        </div>
                    </div>
                )} */}

                {/* Signature Section */}
                <div className="attachment w-100 mb-5">
                    <div className="fileUpload">
                        <div className="form-group">
                            <div>
                                {/* <button
                                    type="button"
                                    className="btn draft"
                                    onClick={() => setShowSignaturePad(!showSignaturePad)}
                                >
                                    e-Signature
                                </button> */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Signature Pad */}
                {showSignaturePad && (
                    <div className='attachment'>
                        <h2 className='text-uppercase'>Signature Pad</h2>
                        <div style={{ border: '1px dashed #b0a1a1', width: '400px', height: '200px', borderRadius: '5px', marginBottom: '10px' }}>
                            <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }}
                            />
                        </div>
                        <div className='d-flex gap-2 mt-2'>
                            <button onClick={clearCanvas} className="btn draft">Clear</button>
                            <button onClick={saveSignature} className="btn btn-save">Save</button>
                        </div>
                    </div>
                )}

                {/* Save All Changes Button */}
                {/* {(affidavitFile || Object.keys(supportedDocuments).length > 0 || signatureImage || filesToDelete.length > 0) && (
                    <div className="attachment mb-5">
                        <div className="text-center">
                            <button
                                className="btn btn-success btn-lg"
                                onClick={saveChanges}
                                disabled={saving}
                            >
                                {saving ? 'Saving Changes...' : 'Save All Changes'}
                            </button>
                            <p className="mt-2 text-muted">
                                Click to save all file changes (uploads, updates, and deletions)
                            </p>
                        </div>
                    </div>
                )} */}
            </div>
        </div>
    );
});

export default AddAttachment;
